'use client';

import { useCallback, useMemo, type FC } from 'react';

import { LINK_NAVIGATION_MODE } from '@/common/constants';
import { useConfigValue } from '@/components/providers/config-provider';
import { useIsCarouselUrl, useRegisterCarouselUrl } from '@/components/ui/carousel-urls-context';
import {
  useLinkLocation,
  useLinkToolName,
  type LinkLocation,
} from '@/components/ui/link-location-context';
import { NewTabHint, readLinkText, useNewTabLabelling } from '@/components/ui/new-tab-hint';
import { useAnalytics, type LinkClickEventData } from '@/hooks/use-analytics';
import { useEnvironmentUrl } from '@/hooks/use-environment-url';
import { useIframeNavigation, type ResolvedLinkLocation } from '@/hooks/use-iframe-navigation';
import {
  linkPositionFor,
  withLinkPosition,
  withoutLinkPosition,
} from '@/lib/analytics/link-position';
import { cn } from '@/lib/utils';
import { isValidHttpUrl } from '@/lib/utils/domain';

/** Link styling shared with the native-anchor path in `MarkdownAnchor`, so a `tel:` link can never
 *  drift into looking unclickable. `text-start` is not here: it is a no-op on an inline anchor and
 *  would fight the tool-UI footer's centering, so only MarkdownLink adds it. */
export const MARKDOWN_LINK_CLASS = 'aui-md-a font-medium text-primary underline underline-offset-4';

// A booked stay's card sits in a carousel but is no sales item, so a reply link to one of its URLs
// must stay a plain markdown link.
const OUTSIDE_CAROUSEL: ReadonlySet<LinkLocation> = new Set(['sidebar', 'reservation-card']);

/** A bare URL in an answer reads as its host: as short as a generic "Link", but it says where the
 *  link goes, to a screen reader listing links too. */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

type MarkdownLinkProps = {
  className?: string;
  href?: string;
  children?: React.ReactNode;
  source?: LinkClickEventData['source'];
} & React.AnchorHTMLAttributes<HTMLAnchorElement>;

export const MarkdownLink: FC<MarkdownLinkProps> = ({
  className,
  href,
  children,
  source,
  target,
  onClick: userOnClick,
  id,
  'aria-label': ariaLabel,
  ...props
}) => {
  // `target="_blank"` always opens in a new tab, regardless of rendering context
  // (e.g. external terms links), mirroring native anchor semantics.
  const forceNewTab = target === '_blank';
  const { getNavigationMode, navigate } = useIframeNavigation();
  const providedLocation = useLinkLocation();
  const toolName = useLinkToolName();
  const baseUrl = useConfigValue<string>('navigation.baseUrl');
  const { processUrl } = useEnvironmentUrl();
  const { trackLinkClick } = useAnalytics();
  const newTabLabelling = useNewTabLabelling({ id, ariaLabel });

  const finalHref = useMemo(() => {
    if (!href) return null;

    try {
      const resolvedUrl = new URL(href, baseUrl ?? undefined).href;
      // A pasted card URL arrives marked; the registry, the report and the marker start clean.
      return withoutLinkPosition(processUrl(resolvedUrl));
    } catch {
      return withoutLinkPosition(processUrl(href));
    }
  }, [href, baseUrl, processUrl]);

  const isInsideCarousel = providedLocation !== null && !OUTSIDE_CAROUSEL.has(providedLocation);
  useRegisterCarouselUrl(isInsideCarousel ? finalHref : null);
  // Only plain markdown links (no provider) need to subscribe to the registry;
  // carousel CTAs and the sidebar already know their location from context, so
  // skipping the subscription avoids re-renders when other carousel items mount.
  const lookupHref = providedLocation === null ? finalHref : null;
  const isRegisteredCarouselUrl = useIsCarouselUrl(lookupHref);
  // Only what is rendered and navigated carries the marker: the registry and the report keep
  // `finalHref`. It follows the real container, so a text link repeating a carousel URL stays text.
  const navigationHref = useMemo(() => {
    if (!finalHref) return null;
    const position = providedLocation ? linkPositionFor(providedLocation) : undefined;
    return withLinkPosition({ url: finalHref, position });
  }, [finalHref, providedLocation]);

  const linkLocation: ResolvedLinkLocation = useMemo(() => {
    if (providedLocation) return providedLocation;
    if (isRegisteredCarouselUrl) return 'carousel';
    return 'text';
  }, [providedLocation, isRegisteredCarouselUrl]);

  const navigationMode = useMemo(
    () => (forceNewTab ? LINK_NAVIGATION_MODE.NEW_TAB : getNavigationMode(linkLocation)),
    [forceNewTab, getNavigationMode, linkLocation],
  );

  const analyticsSource: LinkClickEventData['source'] = source ?? linkLocation;

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      userOnClick?.(e);
      if (e.defaultPrevented || !finalHref) return;

      if (e.metaKey || e.ctrlKey || e.shiftKey) {
        trackLinkClick({
          url: finalHref,
          linkText: readLinkText(e.currentTarget),
          navigationMode: 'new-tab',
          source: analyticsSource,
          ...(toolName && { toolName }),
        });
        return;
      }

      e.preventDefault();
      trackLinkClick({
        url: finalHref,
        linkText: readLinkText(e.currentTarget),
        navigationMode,
        source: analyticsSource,
        ...(toolName && { toolName }),
      });
      navigate({ href: navigationHref ?? finalHref, linkLocation, forceNewTab });
    },
    [
      userOnClick,
      finalHref,
      navigationHref,
      navigate,
      trackLinkClick,
      navigationMode,
      analyticsSource,
      toolName,
      linkLocation,
      forceNewTab,
    ],
  );

  const linkClasses = cn(MARKDOWN_LINK_CLASS, 'text-start', className);

  const displayContent = useMemo(() => {
    const childrenText = children?.toString();
    return childrenText && isValidHttpUrl(childrenText) ? hostOf(childrenText) : children;
  }, [children]);

  const opensInNewTab = navigationMode === 'new-tab';

  return (
    <a
      className={linkClasses}
      href={navigationHref ?? href}
      target={opensInNewTab ? '_blank' : undefined}
      rel={opensInNewTab ? 'noopener noreferrer' : undefined}
      onClick={handleClick}
      {...props}
      id={id}
      aria-label={ariaLabel}
      {...(opensInNewTab ? newTabLabelling.linkProps : undefined)}
    >
      {displayContent}
      {opensInNewTab && <NewTabHint id={newTabLabelling.hintId} />}
    </a>
  );
};
